import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Word-document-style renderer for YadidAi output.
 *
 * The AI writes standard Markdown; this component maps it to actual typography
 * (large bold title, bold section headings, bold subheadings, standard body,
 * proper bullet lists) instead of leaving the source symbols visible. Named
 * "MarkdownDocument" because the output should read like a partner memo, not
 * a chat log.
 *
 * Iter 85.17 · `variant="memo"` opts into an executive-document layout with
 * generous vertical rhythm and heavier heading typography — used by posted
 * meeting briefs so the on-screen render feels like a Word memo. The
 * default variant is unchanged for co-pilot chats and diagnostic memos.
 *
 * Iter 85.18 · The memo variant no longer imposes a bounded 68ch reading
 * column: partners felt the centered strip made memos unnecessarily long
 * vertically inside note cards. The memo now fills the width of its host
 * container (note card / dialog body), which already provide comfortable
 * side padding, so paragraphs and lists reflow across the available
 * horizontal space while remaining responsive on narrow screens.
 *
 * Iter 85.19 · The memo variant now applies Word-document style horizontal
 * inset padding — roughly a 0.75in gutter at large widths (72px) that
 * relaxes responsively on smaller viewports (16 → 32 → 48 → 72px). This
 * gives the memo body, title, section headings and footer a consistent
 * "document margin" feel without narrowing the underlying wider layout.
 *
 * Usage:
 *   <MarkdownDocument>{aiText}</MarkdownDocument>
 *   <MarkdownDocument variant="memo">{aiText}</MarkdownDocument>
 */
export default function MarkdownDocument({ children, className = "", variant = "default" }) {
  const memo = variant === "memo";
  const wrapperClasses = memo
    ? `markdown-doc markdown-doc--memo w-full space-y-6 text-[0.9375rem] leading-[1.75] text-foreground px-4 sm:px-8 md:px-12 lg:px-[4.5rem] ${className}`
    : `markdown-doc space-y-3 text-sm leading-relaxed text-foreground ${className}`;
  return (
    <div className={wrapperClasses} data-testid="markdown-document">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => (
            memo
              ? <h1 className="font-display text-3xl font-bold tracking-tight text-foreground mt-1 mb-1 pb-3 border-b border-border" {...p} />
              : <h1 className="font-display text-xl font-bold tracking-tight text-foreground mt-1 mb-2" {...p} />
          ),
          h2: ({ node, ...p }) => (
            memo
              ? <h2 className="font-display text-lg font-bold tracking-tight text-foreground mt-8 mb-2 uppercase text-[0.8125rem] letter-spacing-widest [letter-spacing:0.08em]" {...p} />
              : <h2 className="font-display text-base font-bold tracking-tight text-foreground mt-4 mb-1.5" {...p} />
          ),
          h3: ({ node, ...p }) => (
            memo
              ? <h3 className="text-base font-bold text-foreground mt-5 mb-2" {...p} />
              : <h3 className="text-sm font-bold text-foreground mt-3 mb-1" {...p} />
          ),
          h4: ({ node, ...p }) => (
            <h4 className="text-sm font-semibold text-foreground/90 mt-2 mb-1" {...p} />
          ),
          p: ({ node, ...p }) => (
            memo
              ? <p className="text-[0.9375rem] leading-[1.75] text-foreground" {...p} />
              : <p className="text-sm leading-relaxed text-foreground/90" {...p} />
          ),
          strong: ({ node, ...p }) => (
            <strong className="font-bold text-foreground" {...p} />
          ),
          em: ({ node, ...p }) => <em className="italic text-foreground/90" {...p} />,
          ul: ({ node, ...p }) => (
            memo
              ? <ul className="list-disc pl-6 space-y-2 marker:text-primary marker:text-lg" {...p} />
              : <ul className="list-disc pl-5 space-y-1 marker:text-primary" {...p} />
          ),
          ol: ({ node, ...p }) => (
            memo
              ? <ol className="list-decimal pl-6 space-y-2 marker:text-primary marker:font-mono" {...p} />
              : <ol className="list-decimal pl-5 space-y-1 marker:text-primary marker:font-mono" {...p} />
          ),
          li: ({ node, ...p }) => (
            memo
              ? <li className="text-[0.9375rem] leading-[1.75]" {...p} />
              : <li className="text-sm leading-relaxed" {...p} />
          ),
          blockquote: ({ node, ...p }) => (
            <blockquote className="border-l-2 border-primary/60 bg-primary/5 pl-3 py-1 text-sm italic text-foreground/80" {...p} />
          ),
          code: ({ node, inline, className: cn, ...p }) =>
            inline ? (
              <code className="font-mono text-[0.75rem] bg-secondary/60 px-1 py-0.5" {...p} />
            ) : (
              <code className="block font-mono text-[0.75rem] bg-secondary/60 p-2 whitespace-pre-wrap" {...p} />
            ),
          hr: () => (
            memo
              ? <hr className="border-border my-8" />
              : <hr className="border-border my-3" />
          ),
          a: ({ node, ...p }) => (
            <a className="text-primary underline hover:text-primary/80" target="_blank" rel="noopener noreferrer" {...p} />
          ),
          table: ({ node, ...p }) => (
            <table className="w-full text-xs border border-border my-2" {...p} />
          ),
          th: ({ node, ...p }) => (
            <th className="text-left px-2 py-1 border-b border-border font-mono uppercase text-[0.625rem] tracking-widest text-muted-foreground" {...p} />
          ),
          td: ({ node, ...p }) => (
            <td className="px-2 py-1 border-b border-border/50 align-top" {...p} />
          ),
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
